import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BitmapText, Container, useApp } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import gsap from 'gsap';

import { gameDrawer } from '../../../../services/gameDrawer';
import { gameService } from '../../../../services/gameService';
import { betReplayService } from '../../../../services/betReplayService';
import { soundService, SoundTypes } from '../../../../services/soundService';

import { gameStore } from '../../../../store/gameStore';
import { winBarStore } from '../../../../store/winBarStore';
import { jackPotStore } from '../../../../store/jackPotStore';
import { userInfoStore } from '../../../../store/userInfoStore';
import { applicationStore } from '../../../../store/applicationStore';

import { Spine } from '../../../../core/classes/spine';
import { isMobile } from '../../../../core/helpers/isMobile';
import { isoStandard } from '../../../../core/helpers/isoStandard';
import { amountContainerInfo } from '../../../../core/constants/amountContainerInfo';
import { parseNumberPrecisionByDoteCount } from '../../../../core/helpers/parseNumberByPrecision';
import { gameGlobalStates } from '../../../../core/constants/reelConstants';

let timeLine;

const JackPotWinPopup = observer(({ winIcon: { winAmount, jackpotCard }, jackpotIcons }) => {
  const { stage } = useApp();
  const bitmapRef = useRef();
  const jackPotWinPopupRef = useRef(null);
  const [popupWinAmount, setPopupWinAmount] = useState(0);
  const [isNumberAnimationEnd, setIsNumberAnimationEnd] = useState(false);
  const { isShowJackPotWinPopup } = jackPotStore;

  const hideJackPotWinPopup = useCallback(() => {
    if (!isShowJackPotWinPopup) return;
    soundService.stop(SoundTypes.JackpotWinPopUp);
    jackPotWinPopupRef.current.children[0].play('end');
    jackPotWinPopupRef.current.children[0].addListener(
      'whiteStart',
      () => {
        soundService.stop(SoundTypes.JackpotBackgroundMusic);
        //soundService.play(SoundTypes.BackgroundMusic, { loop: true });
        jackPotStore.reSetJackpotData();
        gameStore.setGameGlobalState(gameGlobalStates.none);
        applicationStore.setBonusType(null);
        gameStore.setIsSpinStart(false);
        gameService.resetAutoBetState();
        jackPotStore.resetOpenedJackpotCardCount();
        winBarStore.updateShowAmountContainerInfo(amountContainerInfo.winContainer, true);
        winBarStore.setWinAmount(winAmount);
        winBarStore.setLastWinAmount(winAmount);
        applicationStore.setIsShowBlackLayer(false);
        gameDrawer.animateWinAmount({ value: winAmount });
        if (userInfoStore.isReplay) {
          betReplayService.sendReplayMessage();
        }
        gameService.balanceUpdateHandler();
        setPopupWinAmount('0');
      },
      null,
      'end'
    );
  }, [isShowJackPotWinPopup, winAmount]);

  const handleCollectNumbersAndFinishJackPot = useCallback(() => {
    if (!isNumberAnimationEnd && isShowJackPotWinPopup) {
      timeLine.kill();
      setPopupWinAmount(isoStandard(winAmount));
      setIsNumberAnimationEnd(true);
    }
  }, [isNumberAnimationEnd, isShowJackPotWinPopup, winAmount]);

  useEffect(() => {
    const jackPotWinPopup = new Spine({ key: 'jackpotWinPopup' });
    jackPotWinPopup.addChildToSlot('amount', bitmapRef.current);
    jackPotWinPopupRef.current.addChild(jackPotWinPopup);
  }, []);

  useEffect(() => {
    const popup = jackPotWinPopupRef.current.children[0];
    soundService.stop(SoundTypes.JackpotBackgroundMusic);
    soundService.play(SoundTypes.JackpotWinPopUp);
    popup.play('start');
    popup.skeleton.setSkinByName(jackpotIcons[jackpotCard]);
    popup.addListener(
      'complete',
      () => {
        popup.play('loop', true);
        setTimeout(() => {
          setIsNumberAnimationEnd(false);
          hideJackPotWinPopup();
        }, 4000);
      },
      null,
      'start'
    );
  }, [jackpotIcons, jackpotCard, hideJackPotWinPopup]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        handleCollectNumbersAndFinishJackPot();
      }
    },
    [handleCollectNumbersAndFinishJackPot]
  );

  useEffect(() => {
    timeLine = gsap.timeline();
    bitmapRef.current.anchor.set(0.5);
    timeLine.to(bitmapRef.current, {
      textContent: winAmount,
      duration: 10,
      snap: { textContent: 0.1 },
      stagger: {
        each: 0.1,
        onUpdate: function () {
          setPopupWinAmount(
            isoStandard(
              +parseNumberPrecisionByDoteCount(
                +this.targets()[0].textContent,
                1,
                userInfoStore.precision
              )
            )
          );
        },
        onComplete: function () {
          setIsNumberAnimationEnd(true);
        },
      },
    });
  }, [bitmapRef, winAmount]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    stage.interactive = true;
    stage.addEventListener('pointerdown', handleCollectNumbersAndFinishJackPot);
    return () => {
      stage.removeEventListener('pointerdown', handleCollectNumbersAndFinishJackPot);
    };
  }, [handleCollectNumbersAndFinishJackPot, stage]);

  return (
    <Container
      x={isMobile() ? 270 : 720}
      y={isMobile() ? 480 : 405}
      cursor='pointer'
      ref={jackPotWinPopupRef}
      name='JackPotWinPopupContainer'
      visible={isShowJackPotWinPopup}
    >
      <BitmapText
        pivot={0.5}
        align='center'
        ref={bitmapRef}
        text={popupWinAmount}
        style={{ fontSize: isMobile() ? 50 : 80, fontName: 'numbersJackpot' }}
      />
    </Container>
  );
});

export default JackPotWinPopup;
